import { type FC, type ReactNode } from "react";
import { type LinearCstic, type LinearCsticValue, type LinearSubstep, SimpleCsticId } from "../../../Linear/models";
import { useSelector } from "react-redux";
import SubstepFrame from "../../../Linear/components/SubstepFrame";
import { Col, Row } from "../../../../common/components/Layout";
import { CSTIC_PART_SIZING_TYPE_MANUAL } from "../../../Linear/constants";
import LinearInfo from "../../../Linear/components/LinearInfo";
import { SubstepType } from "../../../Linear/dtos";
import { PieceLengthInput } from "../../../Linear/components/Substeps/GuidewaySetStep/components/PieceLengthInput";
import classNames from "classnames";
import LanguageAwareExternalContent from "../../../Linear/components/LanguageAwareExternalContent";
import { Card } from "../../../Linear/components/Card";
import { makeMinMaxLabel } from "../../../Linear/hooks/useALARLogic";
import { Alert } from "~/common/m3/Alert/Alert";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";
import { Stack, styled } from "@mui/material";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import {
  type AlarHelperTextFactory,
  ExtraConfig,
  HelperCstics,
  InputCstics,
} from "~/Configurators/Rail/components/ConfigureRailSubstep/constants";
import { SimpleCsticSelectField } from "~/Configurators/Shaft/components/SimpleCsticSelectField";
import { NumberFormatter as NF } from "~/utils/NumberFormatter";
import { selectMainKmat } from "~/Configurators/Shaft/common/commonShaftHelpers";
import { RAIL_CONFIGURATOR_PRODUCT_CODE, TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE } from "~/Configurators/constants";
import type { RootState } from "~/redux/store";
import { useTranslation } from "react-i18next";
import type { ResolvedCsticDefinition } from "~/Configurators/common/v2/StepDefinition";
import { getMainKmatCstics } from "~/Configurators/common/v2/helper";

interface Props {
  csticIndex: number;
  csticAmount: number;
  def: LinearSubstep;
}

const ConfigureRailSubstep: FC<Props> = ({ def, ...props }) => {
  const cstics = useSelector(selectMainKmat).cstics;

  return (
    <SubstepFrame
      title={def.name}
      description={def.description}
      info={def.info}
      isPicked={true}
      showError={false}
      errorMessage={""}
      {...props}
    >
      <div className={"w-100 mb-30"}>
        <Row className={"d-flex"}>
          <Col size={4}>
            <SingleGuidewayTile index={0} def={def} cstics={cstics} />
          </Col>
        </Row>
      </div>
    </SubstepFrame>
  );
};
export default ConfigureRailSubstep;

interface TileProps {
  index: number;
  def: LinearSubstep;
  cstics: Record<string, LinearCstic>;
}

const SingleGuidewayTile = (props: TileProps) => {
  const { def, index, cstics } = props;

  const xmlT = useXmlConfiguratorTranslations().t!;
  const { t } = useTranslation();

  const { input: inputCstics, helper: helperCstics } = getMainKmatCstics(def);

  const minCsticName = helperCstics[HelperCstics.totalLengthMin]?.name;
  const minLength = cstics[minCsticName]?.value;
  const maxCsticName = helperCstics[HelperCstics.totalLengthMax]?.name;
  const maxLength = cstics[maxCsticName]?.value;

  const formatValue = (val: string | undefined) => {
    return val ? NF.getDefaultFormatter().format(+val) : "N/A";
  };
  const lengthHelperLabel = `${formatValue(minLength)}mm - ${formatValue(maxLength)}mm`;

  const totalLengthDef = inputCstics[InputCstics.TotalLength];

  const partAmountCsticDef = inputCstics[InputCstics.partAmount];
  const partAmountCstic = cstics[partAmountCsticDef.name];

  const partSizingCsticDef = inputCstics[InputCstics.partSizingType];
  const partSizingCstic = cstics[partSizingCsticDef.name];
  const isCustomPieceSizes = partSizingCstic?.value === CSTIC_PART_SIZING_TYPE_MANUAL;

  const renderDropdownEntry = (value: LinearCsticValue) => <LanguageAwareExternalContent content={value.label} />;

  const alarVisible = inputCstics[InputCstics.aL].visible && inputCstics[InputCstics.aR].visible;

  const makeGetValue = (source: Record<string, ResolvedCsticDefinition>) => (csticName: string) => {
    const stringVal = cstics[source[csticName]?.name]?.value;
    if (stringVal) {
      return +stringVal;
    } else {
      return undefined;
    }
  };
  const getHelperValue = makeGetValue(helperCstics);
  const getValue = makeGetValue(inputCstics);

  const alarHelperData = [
    {
      recommendedMin: getHelperValue(HelperCstics.alRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.alRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.alHardMax),
      value: getValue(InputCstics.aL) ?? null,
      t,
    },
    {
      recommendedMin: getHelperValue(HelperCstics.arRecommendedMin),
      recommendedMax: getHelperValue(HelperCstics.arRecommendedMax),
      hardMin: 0,
      hardMax: getHelperValue(HelperCstics.arHardMax),
      value: getValue(InputCstics.aR) ?? null,
      t,
    },
  ];

  const helperTextFactory = def.extraConfig[ExtraConfig.AlarHelperTextFactory] as AlarHelperTextFactory;
  const alHelperText = helperTextFactory(alarHelperData[0]);
  const arHelperText = helperTextFactory(alarHelperData[1]);

  let lengthDecimals;
  let alarDecimals;
  const productCode = useSelector((state: RootState) => state.linearConfigurator.productCode);

  if (productCode === RAIL_CONFIGURATOR_PRODUCT_CODE || productCode === TKLT_RAIL_CONFIGURATOR_PRODUCT_CODE) {
    lengthDecimals = 1;
    alarDecimals = 2;
  } else {
    lengthDecimals = 0;
    alarDecimals = 1;
  }

  let showNotRecommendedWarning = (def.extraConfig[ExtraConfig.showNotRecommendedWarning] ?? false) as boolean;
  if (showNotRecommendedWarning) {
    showNotRecommendedWarning = alarHelperData.some((helperData) => {
      if (!helperData.value || !helperData.recommendedMin || !helperData.recommendedMax) {
        return false;
      }
      return helperData.value < helperData.recommendedMin || helperData.value > helperData.recommendedMax;
    });
  }

  return (
    <Card disableHover={true}>
      <StyledStack gap={2}>
        {def.extraTexts["upperMessage"] && (
          <Alert severity={"info"}>
            <LanguageAwareExternalContent content={def.extraTexts["upperMessage"]} />
          </Alert>
        )}

        <LinearSection label={xmlT(def.extraTexts["lengthName"])} info={def.extraTexts["lengthInfo"]}>
          <SimpleCsticNumberField
            csticId={SimpleCsticId(totalLengthDef.name)}
            substepType={SubstepType.ConfigureRail}
            disabled={totalLengthDef.disabled}
            helperLabel={lengthHelperLabel}
            required={totalLengthDef.required}
            decimalScale={lengthDecimals}
          />
        </LinearSection>

        <Alert severity={"info"} className={"pb-3"}>
          <LanguageAwareExternalContent content={def.extraTexts["lowerMessage"]} />
        </Alert>

        {alarVisible && (
          <>
            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["alarName"]}
                  loadedClassname={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["alarInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={SimpleCsticId(inputCstics[InputCstics.aL].name)}
                    substepType={SubstepType.ConfigureRail}
                    disabled={isCustomPieceSizes}
                    required={inputCstics[InputCstics.aL].required}
                    decimalScale={alarDecimals}
                    helperLabel={alHelperText}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticNumberField
                    useCsticLabel
                    csticId={SimpleCsticId(inputCstics[InputCstics.aR].name)}
                    substepType={SubstepType.ConfigureRail}
                    disabled={isCustomPieceSizes || inputCstics[InputCstics.aR].disabled}
                    required={inputCstics[InputCstics.aR].required}
                    decimalScale={alarDecimals}
                    helperLabel={arHelperText}
                  />
                </Col>
              </Row>
              {showNotRecommendedWarning && (
                <Alert severity={"info"} className={"mt-3"}>
                  <LanguageAwareExternalContent content={def.extraTexts["alarWarningMessage"]} />
                </Alert>
              )}
            </LinearSection>

            <LinearSection
              label={
                <LanguageAwareExternalContent
                  content={def.extraTexts["partsSectionName"]}
                  loadedClassname={"scha__lc__external-label"}
                />
              }
              info={def.extraTexts["partsSectionInfo"]}
            >
              <Row>
                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={SimpleCsticId(partSizingCsticDef.name)}
                    disabled={partSizingCsticDef.disabled}
                    substepType={SubstepType.ConfigureRail}
                    renderValue={renderDropdownEntry}
                  />
                </Col>

                <Col size={6}>
                  <SimpleCsticSelectField
                    useCsticLabel
                    csticId={SimpleCsticId(partAmountCsticDef.name)}
                    disabled={partAmountCsticDef.disabled}
                    substepType={SubstepType.ConfigureRail}
                    renderValue={renderDropdownEntry}
                  />
                </Col>
              </Row>
              {PIECE_INPUTS.filter((csticName) => inputCstics[csticName]?.visible).map((csticName, partIndex) => (
                <PieceLengthInput
                  key={csticName}
                  cstics={cstics}
                  kmatIndex={index}
                  pieceIndex={partIndex}
                  disabled={inputCstics[csticName].disabled}
                  isSecondLast={false}
                  isLast={partIndex === PIECE_INPUTS.length - 1}
                  substep={SubstepType.ConfigureRail}
                  makeMinMaxLabelFormatter={makeMinMaxLabel}
                />
              ))}
            </LinearSection>
          </>
        )}
      </StyledStack>
    </Card>
  );
};

const PIECE_INPUTS = [
  InputCstics.partOne,
  InputCstics.partTwo,
  InputCstics.partThree,
  InputCstics.partFour,
  InputCstics.partFive,
];

const StyledStack = styled(Stack)({
  padding: "16px",
});

const LinearSection: FC<{ label: ReactNode; info?: string; className?: string; children?: ReactNode }> = ({
  children,
  label,
  info,
  className,
}) => {
  return (
    <div className={classNames("mb-30", className)}>
      <LinearSectionTitle className={"mt-0 mb-2"} info={info}>
        {label}
      </LinearSectionTitle>
      {children}
    </div>
  );
};

export const LinearSectionTitle: FC<{ info?: string } & JSX.IntrinsicElements["p"]> = ({
  info,
  className,
  children,
  // eslint-disable-next-line unused-imports/no-unused-vars
  ...rest
}) => {
  return (
    <p className={classNames("title-medium d-flex flex-align-center", className)}>
      <span>{children}</span>
      <LinearInfo
        className={"ml-1 flex-align-self-start"}
        popupClassName={"scha__lc__external-content--shaft"}
        info={info}
      />
    </p>
  );
};
